import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';

import {
  createUser,
  fetchPermissionCatalog,
  listCompanyOptions,
  listFacilityOptions,
  listUsers,
  replaceUserPermissions,
  resetUserPassword,
  updateUser,
  type CompanyOption,
  type FacilityOption,
} from './api';
import type {
  CatalogPermissionDto,
  EfsUser,
  PasswordResetInput,
  UserCreateInput,
  UserUpdateInput,
} from './types';

const KEY = 'users' as const;

export const useUsersList = (params: { q?: string; active_only?: boolean } = {}) => {
  return useQuery<EfsUser[], ApiClientError>({
    queryKey: [KEY, 'list', params],
    queryFn: () => listUsers(params),
    placeholderData: (prev) => prev,
  });
};

export const useCompanyOptions = (enabled = true) => {
  return useQuery<CompanyOption[], ApiClientError>({
    queryKey: ['companies', 'options'],
    queryFn: listCompanyOptions,
    enabled,
    staleTime: 60_000,
  });
};

export const useFacilityOptions = (enabled = true) => {
  return useQuery<FacilityOption[], ApiClientError>({
    queryKey: ['facilities', 'options'],
    queryFn: listFacilityOptions,
    enabled,
    staleTime: 60_000,
  });
};

export const useCreateUser = () => {
  const qc = useQueryClient();
  return useMutation<EfsUser, ApiClientError, UserCreateInput>({
    mutationFn: createUser,
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useUpdateUser = () => {
  const qc = useQueryClient();
  return useMutation<EfsUser, ApiClientError, { id: string; body: UserUpdateInput }>({
    mutationFn: ({ id, body }) => updateUser(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useResetUserPassword = () => {
  return useMutation<void, ApiClientError, { id: string; body: PasswordResetInput }>({
    mutationFn: ({ id, body }) => resetUserPassword(id, body),
  });
};

export const usePermissionCatalog = (companyId?: string, opts: { enabled?: boolean } = {}) => {
  return useQuery<CatalogPermissionDto[], ApiClientError>({
    queryKey: ['permission-catalog', companyId ?? 'own'],
    queryFn: () => fetchPermissionCatalog(companyId),
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
  });
};

export const useReplaceUserPermissions = () => {
  const qc = useQueryClient();
  return useMutation<EfsUser, ApiClientError, { id: string; permission_codes: string[] }>({
    mutationFn: ({ id, permission_codes }) => replaceUserPermissions(id, permission_codes),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};
